import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import { COMPARE_PALETTES, getLapColor, LAP_COLORS } from '../LapSelector/LapSelector';
import { lapDisplayNumber } from '../../utils/lapDelta';
import { convertDistanceFt, convertValue, getDisplayUnit, getDistanceUnit } from '../../utils/unitConvert';
import { lastCursor } from '../../utils/chartCursor';
import { canExportVideo } from '../../utils/canvasRecording';
import { readTrackDifferencePrefs, writeTrackDifferencePrefs } from '../../utils/trackDifferencePrefs';
import { exportCapturedVideo } from '../../services/api';
import { SF_MISMATCH_WARN_M } from '../../utils/sfAlignment';
import {
  buildTrackDifferenceModel,
  HEIGHT_MODES,
  pickDefaultTrackDifferencePair,
  stationAtDistanceFt,
  stationAtElapsed,
} from '../../utils/trackDifference';
import {
  bucketRibbon,
  buildRibbon,
  CAMERA_PRESETS,
  DEFAULT_VIEW,
  ELEVATION_SCALE,
  EXAGGERATIONS,
  followCenter,
  fractionalStationAt,
  FOLLOW_ZOOM,
  headlineFor,
  lerpPoint,
  markerZoom,
  projectionCenter,
  projectTrack,
  RIBBON_STROKE,
  scaleLabel,
  sectorReadout,
  sectorStates,
  viewportRect,
  VIEW_BOX,
  WHEEL_ZOOM_RATE,
  ZOOM_LIMITS,
} from '../../utils/trackDifferenceRender';
import styles from './TrackDifference.module.css';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
// Pointer travel below this counts as a click, not a rotate or a pan.
const CLICK_SLOP_PX = 4;
const finite = value => Number.isFinite(Number(value));

function lapOption({
  driverKey, session, lapTraces, selectedLaps, name, palette, customColors,
  fallbackColor, sfOffsetM, fetchTrace, loadingTraces,
}) {
  return (session?.laps || [])
    .filter(lap => !lap.is_outlap && !lap.is_inlap && finite(lap.lap_time_seconds))
    .map((lap, order) => {
      const lapIndex = lap.index;
      const trace = lapTraces?.[lapIndex] || null;
      const lapNo = lapDisplayNumber(session?.laps, lapIndex, lapIndex + 1);
      return {
        key: `${session?.session_id || driverKey}:${lapIndex}`,
        driverKey,
        sessionId: session?.session_id,
        lapIndex,
        lapNo,
        name,
        accent: getLapColor(lapIndex, selectedLaps, palette, customColors)
          || fallbackColor || palette?.[order % palette.length] || '#ffffff',
        sfOffsetM,
        trace,
        timeS: trace?.lap_time_seconds ?? lap.lap_time_seconds,
        timeLabel: trace?.lap_time_str || lap.lap_time_str,
        isLoading: loadingTraces?.has(lapIndex) || false,
        fetchTrace: fetchTrace ? () => fetchTrace(lapIndex) : null,
      };
    })
    .filter(Boolean);
}

export function collectTrackDifferenceLaps({
  session, lapTraces, selectedLaps, driverName1, compareDrivers = [], customColors1,
  primarySfOffsetM, onFetchTrace, loadingTraces,
}) {
  const options = lapOption({
    driverKey: 'p', session, lapTraces, selectedLaps,
    name: driverName1 || session?.meta?.racer || 'Driver 1',
    palette: LAP_COLORS, customColors: customColors1, fallbackColor: LAP_COLORS[0], sfOffsetM: primarySfOffsetM,
    fetchTrace: onFetchTrace, loadingTraces,
  });
  compareDrivers.forEach((driver, slot) => {
    options.push(...lapOption({
      driverKey: `c${slot}`,
      session: driver.session,
      lapTraces: driver.lapTraces,
      selectedLaps: driver.selectedLaps,
      name: driver.name || `Driver ${slot + 2}`,
      palette: driver.palette || COMPARE_PALETTES[slot] || COMPARE_PALETTES[0],
      customColors: driver.customColors,
      fallbackColor: driver.accent,
      sfOffsetM: driver.sfOffsetM,
      fetchTrace: driver.fetchTrace,
      loadingTraces: driver.loadingTraces,
    }));
  });
  return options;
}

function optionLabel(lap) {
  const time = lap.timeLabel || `${Number(lap.timeS).toFixed(3)}s`;
  return `${lap.name} · Lap ${lap.lapNo} · ${time}`;
}

function groupedLapOptions(options, excludedKey) {
  const groups = [];
  for (const option of options.filter(item => item.key !== excludedKey)) {
    let group = groups.find(item => item.driverKey === option.driverKey);
    if (!group) {
      group = { driverKey: option.driverKey, name: option.name, laps: [] };
      groups.push(group);
    }
    group.laps.push(option);
  }
  return groups.map(group => (
    <optgroup key={group.driverKey} label={group.name}>
      {group.laps.map(item => <option key={item.key} value={item.key}>{optionLabel(item)}</option>)}
    </optgroup>
  ));
}

/**
 * Batch the bucketed ribbon into path strings: one road/fill/edge path per
 * color per depth bucket. Playback then re-renders a few dozen `d` attributes
 * a frame instead of reconciling an element per station.
 */
function buildRibbonLayers(projection, segments, activeThrough) {
  const { road, top } = projection;
  const { buckets, clipped } = bucketRibbon(projection, segments, activeThrough);
  const pt = point => `${point.x.toFixed(1)},${point.y.toFixed(1)}`;
  const edgeOf = index => `M${pt(top[index])}L${pt(top[index + 1])}`;
  const quadOf = index => `M${pt(road[index])}L${pt(road[index + 1])}`
    + `L${pt(top[index + 1])}L${pt(top[index])}Z`;
  const join = (map, toCommand) => new Map(
    [...map].map(([color, list]) => [color, list.map(toCommand).join('')]),
  );
  return {
    buckets: buckets.map(bucket => ({
      road: bucket.road.map(index => `M${pt(road[index])}L${pt(road[index + 1])}`).join(''),
      fill: join(bucket.active, quadOf),
      edge: join(bucket.active, edgeOf),
      // Dim fill as well as edge: a bare floating line at ribbon height reads as
      // a stray graph rather than as the same object, not yet reached.
      ghostFill: join(bucket.ghost, quadOf),
      ghost: join(bucket.ghost, edgeOf),
    })),
    clippedPath: clipped.map(edgeOf).join(''),
  };
}

function gapLabel(station, lapA, lapB) {
  if (!station || Math.abs(station.gapSec) < 0.005) return { lead: 'EVEN', value: '0.000 s' };
  const leader = station.gapSec > 0 ? lapA : lapB;
  return { lead: `${leader.name} AHEAD`, value: `${Math.abs(station.gapSec).toFixed(3)} s` };
}

export function TrackDifferenceView({
  lapA, lapB, sectors = [], elevationProfile = [], unitPrefs = {}, compact = false,
}) {
  const svgId = useId().replace(/:/g, '');
  const markerId = `${svgId}-marker`;
  const model = useMemo(
    () => buildTrackDifferenceModel(lapA, lapB, {
      samples: compact ? 260 : 420,
      sectors,
      elevationProfile,
    }),
    // App rebuilds lightweight driver descriptors during ordinary window
    // layout updates. Key the expensive model to the actual lap traces so a
    // panel drag cannot restart an in-progress replay.
    [lapA?.key, lapA?.trace, lapB?.key, lapB?.trace, sectors, elevationProfile, compact],
  );
  const restored = useRef(readTrackDifferencePrefs()).current;
  const [viewAngles, setViewAngles] = useState(restored.viewAngles || DEFAULT_VIEW);
  const [rotating, setRotating] = useState(false);
  const [camera, setCamera] = useState({
    zoom: restored.zoom, centerX: null, centerY: null, follow: restored.follow,
  });
  const svgRef = useRef(null);
  const [heightMode, setHeightMode] = useState(restored.heightMode);
  const [exaggeration, setExaggeration] = useState(restored.exaggeration);
  const [sectorsByLoss, setSectorsByLoss] = useState(restored.sectorsByLoss);

  // Remembered for the next mount, which in this app happens whenever another
  // panel is maximized rather than only when the user leaves.
  useEffect(() => {
    writeTrackDifferencePrefs({
      heightMode, exaggeration, sectorsByLoss, viewAngles,
      zoom: camera.zoom, follow: camera.follow,
    });
  }, [heightMode, exaggeration, sectorsByLoss, viewAngles, camera.zoom, camera.follow]);
  const dragRef = useRef(null);

  const ribbon = useMemo(() => (model ? buildRibbon(model, {
    heightMode, exaggeration, colorA: lapA.accent, colorB: lapB.accent,
  }) : null), [model, heightMode, exaggeration, lapA.accent, lapB.accent]);
  const fullScale = ribbon?.fullScale ?? HEIGHT_MODES.gap.referenceScale;

  const projection = useMemo(
    () => projectTrack(model?.stations, viewAngles.yaw, viewAngles.pitch, ribbon?.lifts),
    [model, viewAngles, ribbon],
  );
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(2);
  const [highlight, setHighlight] = useState(null);
  const frameRef = useRef(null);
  const lastFrameRef = useRef(null);
  const elapsedRef = useRef(0);
  const [exportJob, setExportJob] = useState(null);
  const [exportNote, setExportNote] = useState(null);
  const cancelExport = useRef(false);

  // Where the camera actually looks: the pan centre unless Follow is on, in
  // which case the lap drives it. Resolved here so the wheel, the drag handler
  // and the viewBox all read one answer.
  const home = useMemo(
    () => (projection ? projectionCenter(projection) : null),
    [projection],
  );
  const cameraView = useMemo(() => {
    if (!projection || !home) return { zoom: 1, centerX: 0, centerY: 0 };
    const followAt = camera.follow
      ? followCenter(projection, model.stations, elapsed)
      : null;
    return {
      zoom: camera.zoom,
      centerX: followAt?.x ?? camera.centerX ?? home.x,
      centerY: followAt?.y ?? camera.centerY ?? home.y,
    };
  }, [projection, home, model, camera, elapsed]);
  const view = viewportRect(cameraView);
  // Follow re-resolves the camera every frame, so the wheel handler reads the
  // current value through a ref rather than being torn down and rebound 60
  // times a second.
  const cameraViewRef = useRef(cameraView);
  cameraViewRef.current = cameraView;

  // Wheel must be a native non-passive listener: React's own wheel binding is
  // passive, so preventDefault there is ignored and the page scrolls instead.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      const current = cameraViewRef.current;
      const next = clamp(
        current.zoom * Math.exp(-event.deltaY * WHEEL_ZOOM_RATE),
        ZOOM_LIMITS.min, ZOOM_LIMITS.max,
      );
      if (next === current.zoom) return;
      // Keep whatever is under the pointer under the pointer. getScreenCTM
      // already accounts for the letterboxing preserveAspectRatio introduces,
      // which measuring the element rect by hand would not.
      const ctm = svg.getScreenCTM?.();
      const rect = viewportRect(current);
      let anchorX = rect.x + rect.width / 2;
      let anchorY = rect.y + rect.height / 2;
      if (ctm) {
        const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(ctm.inverse());
        anchorX = point.x;
        anchorY = point.y;
      }
      const u = rect.width ? (anchorX - rect.x) / rect.width : 0.5;
      const v = rect.height ? (anchorY - rect.y) / rect.height : 0.5;
      const nextWidth = VIEW_BOX.width / next;
      const nextHeight = VIEW_BOX.height / next;
      setCamera(current => ({
        ...current,
        zoom: next,
        centerX: anchorX - u * nextWidth + nextWidth / 2,
        centerY: anchorY - v * nextHeight + nextHeight / 2,
        follow: next === ZOOM_LIMITS.min ? false : current.follow,
      }));
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, []);

  // Left drags the camera around the track, right (or shift) slides it across —
  // the orbit/pan split Lap3D and every CAD viewer already train people on.
  const beginDrag = (event) => {
    if (event.button !== 0 && event.button !== 2) return;
    const panning = event.button === 2 || event.shiftKey;
    // Screen pixels per view unit, so a pan drag tracks the cursor exactly.
    const scale = svgRef.current?.getScreenCTM?.()?.a || 1;
    dragRef.current = {
      pointerId: event.pointerId,
      panning,
      scale,
      x: event.clientX,
      y: event.clientY,
      yaw: viewAngles.yaw,
      pitch: viewAngles.pitch,
      camera: cameraView,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setRotating(true);
  };
  const drag = (event) => {
    const start = dragRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    if (start.panning) {
      // Taking hold of the view is an instruction to stop being driven by it.
      setCamera(current => ({
        ...current,
        follow: false,
        centerX: start.camera.centerX - (event.clientX - start.x) / start.scale,
        centerY: start.camera.centerY - (event.clientY - start.y) / start.scale,
      }));
      return;
    }
    setViewAngles({
      yaw: start.yaw + (event.clientX - start.x) * 0.38,
      pitch: clamp(start.pitch - (event.clientY - start.y) * 0.28, 18, 88),
    });
  };
  const endDrag = (event) => {
    const start = dragRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    const travelled = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    dragRef.current = null;
    setRotating(false);
    // A click rather than a drag: jump to that point on the road and tell the
    // other panels. The slop is what keeps a rotate or a pan from also seeking.
    if (travelled <= CLICK_SLOP_PX) seekToPointer(event);
  };

  /** Nearest station to a pointer event, in the SVG's own coordinates. */
  const seekToPointer = (event) => {
    const svg = svgRef.current;
    const road = projection?.road;
    if (!svg?.getScreenCTM || !road?.length || !model) return;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(ctm.inverse());
    let best = 0;
    let bestDist = Infinity;
    for (let index = 0; index < road.length; index += 1) {
      const dist = (road[index].x - point.x) ** 2 + (road[index].y - point.y) ** 2;
      if (dist < bestDist) { bestDist = dist; best = index; }
    }
    const station = model.stations[best];
    if (!station) return;
    setPlaying(false);
    seek(station.clockSec, false);
    publishedRef.current = station.index;
    publishStation(station, 'trackapp:chart-click');
  };

  const resetView = () => {
    setViewAngles(DEFAULT_VIEW);
    setCamera({ zoom: 1, centerX: null, centerY: null, follow: false });
  };

  useEffect(() => {
    setElapsed(0);
    elapsedRef.current = 0;
    publishedRef.current = -1;
    setPlaying(false);
  }, [model]);

  const station = useMemo(() => stationAtElapsed(model, elapsed), [model, elapsed]);

  const publishStation = useCallback((next, eventName = 'trackapp:chart-hover') => {
    if (!next || typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(eventName, {
      detail: {
        lat: next.lat,
        lon: next.lon,
        lat2: next.latB,
        lon2: next.lonB,
        distFt: next.distFt,
        videoTime: lapA?.driverKey === 'p'
          ? next.csvTimeA
          : lapB?.driverKey === 'p' ? next.csvTimeB : next.csvTimeA,
        source: 'track-difference',
      },
    }));
  }, [lapA?.driverKey, lapB?.driverKey]);

  // Cross-panel position is resolved per station, so broadcasting every frame
  // sent the same value 60 times a second and woke every other panel with it.
  // The station only actually changes about ten times a second.
  const publishedRef = useRef(-1);
  const seek = useCallback((nextElapsed, publish = true) => {
    if (!model) return;
    const value = clamp(Number(nextElapsed) || 0, 0, model.durationSec);
    elapsedRef.current = value;
    setElapsed(value);
    if (!publish) return;
    const next = stationAtElapsed(model, value);
    if (next && next.index !== publishedRef.current) {
      publishedRef.current = next.index;
      publishStation(next);
    }
  }, [model, publishStation]);

  useEffect(() => {
    if (!playing || !model) return undefined;
    const tick = (now) => {
      const previous = lastFrameRef.current ?? now;
      lastFrameRef.current = now;
      const next = elapsedRef.current + ((now - previous) / 1000) * rate;
      if (next >= model.durationSec) {
        seek(model.durationSec);
        setPlaying(false);
        lastFrameRef.current = null;
        return;
      }
      seek(next);
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [playing, model, rate, seek]);

  useEffect(() => {
    if (!model || typeof window === 'undefined') return undefined;
    const followCursor = (event) => {
      if (event.detail?.source === 'track-difference' || !finite(event.detail?.distFt)) return;
      const next = stationAtDistanceFt(model, event.detail.distFt);
      if (next) seek(next.clockSec, false);
    };
    const followVideo = (event) => {
      if (!finite(event.detail?.distFt)) return;
      const next = stationAtDistanceFt(model, event.detail.distFt);
      if (next) seek(next.clockSec, false);
      if (event.detail?.playing) setPlaying(false);
    };
    const followCorner = (event) => {
      const { distFtMin, distFtMax } = event.detail || {};
      setHighlight(finite(distFtMin) && finite(distFtMax) ? { min: Number(distFtMin), max: Number(distFtMax) } : null);
    };
    window.addEventListener('trackapp:chart-hover', followCursor);
    window.addEventListener('trackapp:chart-click', followCursor);
    window.addEventListener('trackapp:video-time', followVideo);
    window.addEventListener('trackapp:corner-highlight', followCorner);
    const cursor = lastCursor();
    if (cursor) followCursor({ detail: cursor });
    return () => {
      window.removeEventListener('trackapp:chart-hover', followCursor);
      window.removeEventListener('trackapp:chart-click', followCursor);
      window.removeEventListener('trackapp:video-time', followVideo);
      window.removeEventListener('trackapp:corner-highlight', followCorner);
    };
  }, [model, seek]);

  // The encoder and the Canvas2D renderer are only needed once someone exports,
  // so they stay out of the panel's own chunk.
  const runExport = async () => {
    if (!model || exportJob) return;
    setPlaying(false);
    setExportNote(null);
    cancelExport.current = false;
    setExportJob({ phase: 'render', done: 0, total: 0 });
    try {
      const [{ createTrackDifferenceRenderer }, { bitrateFor, ExportCancelled, recordFrames }] =
        await Promise.all([
          import('./trackDifferenceFrame'),
          import('../../utils/frameRecorder'),
        ]);
      const width = 1920;
      const height = 1080;
      const fps = 30;
      const renderer = createTrackDifferenceRenderer(model, {
        width, height, fps, rate, lapA, lapB, heightMode, exaggeration, viewAngles, unitPrefs,
        // The clip is the view you set up, Follow included.
        zoom: cameraView.zoom,
        follow: camera.follow,
        subtitle: `${lapA.name} lap ${lapA.lapNo} vs ${lapB.name} lap ${lapB.lapNo}`,
      });
      try {
        const { blob, container, frames } = await recordFrames({
          canvas: renderer.canvas,
          renderFrame: renderer.drawFrame,
          total: renderer.frames,
          width, height, fps,
          bitrate: bitrateFor(width, height),
          onProgress: (done, total) => setExportJob({ phase: 'render', done, total }),
          isCancelled: () => cancelExport.current,
        });
        setExportJob({ phase: 'encode', done: frames, total: frames });
        const stem = `track-difference-${lapA.name}-lap${lapA.lapNo}-vs-${lapB.name}-lap${lapB.lapNo}`;
        const { blob: mp4, encoded } = await exportCapturedVideo(blob, {
          fps, frames, filename: stem, container,
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(mp4);
        link.download = `${stem}.mp4`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 5000);
        // A short recording is not a visible gap - the server re-times purely by
        // frame index, so missing frames come out as a clip that plays
        // fractionally fast. Say so rather than hand over a silently wrong one.
        if (encoded && Math.abs(encoded - frames) > Math.max(1, frames / 200)) {
          setExportNote(`Recorded ${encoded} of ${frames} frames \u2014 the clip runs slightly fast.`);
        }
      } catch (error) {
        if (!(error instanceof ExportCancelled)) throw error;
      }
    } catch (error) {
      setExportNote(error?.message ? String(error.message).slice(0, 200) : 'Export failed.');
    } finally {
      setExportJob(null);
      cancelExport.current = false;
    }
  };

  if (!model || !projection || !ribbon) {
    return <div className={styles.empty}>Choose two loaded, timed laps to compare.</div>;
  }

  const gap = gapLabel(station, lapA, lapB);
  const speedUnit = getDisplayUnit('mph', unitPrefs);
  const speedA = finite(station?.speedA) ? convertValue(station.speedA, 'mph', unitPrefs) : null;
  const speedB = finite(station?.speedB) ? convertValue(station.speedB, 'mph', unitPrefs) : null;
  const speedDiff = speedA != null && speedB != null ? speedA - speedB : null;
  const groundPath = projection.ground.map(point => `${point.x},${point.y}`).join(' ');
  const elevationDistance = convertDistanceFt(model.elevationRangeM * 3.28084, unitPrefs);
  const elevationUnit = getDistanceUnit(unitPrefs);

  const headline = headlineFor(station, { heightMode, unitPrefs, lapA, lapB });

  // Sub-station cursor: the marker, its wall and the ribbon's leading edge all
  // advance continuously rather than a station at a time.
  const position = fractionalStationAt(model.stations, elapsed);
  const wholeStation = Math.min(model.stations.length - 2, Math.floor(position));
  const stationFrac = position - wholeStation;
  const roadPoint = lerpPoint(projection.road, wholeStation, stationFrac);
  const topPoint = lerpPoint(projection.top, wholeStation, stationFrac);
  const leading = ribbon.segments[wholeStation];
  const lapFraction = position / Math.max(1, model.stations.length - 1);

  /** Jump to a point on the lap given as a 0..1 fraction of it. */
  const seekToFraction = (fraction) => {
    const target = model.stations[Math.round(
      clamp(fraction, 0, 1) * (model.stations.length - 1),
    )];
    if (!target) return;
    seek(target.clockSec, false);
    publishedRef.current = target.index;
    publishStation(target, 'trackapp:chart-click');
  };

  const sectorRows = sectorStates(model.sectors, lapFraction);
  // "Biggest first" answers what to work on rather than what happened in order,
  // the same trade Head-to-Head's corner table offers.
  const orderedSectors = sectorsByLoss
    ? [...sectorRows].sort((x, y) => Math.abs(y.gapSec) - Math.abs(x.gapSec))
    : sectorRows;

  const { buckets, clippedPath } = buildRibbonLayers(projection, ribbon.segments, wholeStation);
  const label = scaleLabel(fullScale, heightMode, unitPrefs);
  // The ruler is the whole point of a fixed scale: it turns ribbon height from
  // a mood into a reading, and it is what makes `auto` honest.
  const rulerPx = ribbon.maxLiftM * projection.pxPerLiftM;
  // Every stroke here is `vector-effect: non-scaling-stroke`, i.e. sized in
  // screen pixels rather than track units, so zooming grows the track and leaves
  // the road a thread laid across it unless the weights grow to match.
  const sw = value => value * view.zoom;
  // The marker is a cursor rather than scenery, so it grows sub-linearly: its
  // radius is in user units (which scale with the viewBox) and its stroke is
  // not, hence the two directions here.
  const markerR = value => (value * markerZoom(view.zoom)) / view.zoom;
  const markerSw = value => value * markerZoom(view.zoom);
  const roadPath = projection.road.map(point => `${point.x},${point.y}`).join(' ');
  const lineBPath = projection.lineB.map(point => `${point.x},${point.y}`).join(' ');
  const highlightPath = highlight
    ? model.stations.slice(1).reduce((commands, item, index) => (
      item.distFt >= highlight.min && item.distFt <= highlight.max
        ? `${commands}M${projection.road[index].x.toFixed(1)},${projection.road[index].y.toFixed(1)}`
          + `L${projection.road[index + 1].x.toFixed(1)},${projection.road[index + 1].y.toFixed(1)}`
        : commands), '')
    : '';

  return (
    <div className={`${styles.view} ${compact ? styles.compact : ''}`}>
      <div className={styles.stage}>
        <div className={styles.hudTop}>
          <div className={styles.driverCard} style={{ '--driver-color': lapA.accent }}>
            <span className={styles.driverName}>{lapA.name}</span>
            <span className={styles.lapLabel}>Lap {lapA.lapNo}</span>
            <strong>{speedA == null ? '—' : Math.round(speedA)} <small>{speedUnit}</small></strong>
          </div>
          <div className={styles.speedDelta}>
            <span>{headline.label}</span>
            <strong>{headline.text}</strong>
            <small>{headline.unit} · {headline.leader}</small>
          </div>
          <div className={styles.driverCard} style={{ '--driver-color': lapB.accent }}>
            <span className={styles.driverName}>{lapB.name}</span>
            <span className={styles.lapLabel}>Lap {lapB.lapNo}</span>
            <strong>{speedB == null ? '—' : Math.round(speedB)} <small>{speedUnit}</small></strong>
          </div>
        </div>

        <div className={styles.viewHint}>
          <span>Drag to rotate · right-drag to pan · scroll to zoom</span>
          {model.elevationRangeM > 0.5
            ? <span>Elevation range {Math.round(elevationDistance)} {elevationUnit} · terrain {ELEVATION_SCALE}×</span>
            : <span>Elevation unavailable</span>}
        </div>
        <svg
          className={`${styles.track} ${rotating ? styles.trackRotating : ''}`}
          viewBox={`${view.x.toFixed(2)} ${view.y.toFixed(2)} ${view.width.toFixed(2)} ${view.height.toFixed(2)}`}
          role="img"
          aria-label={`Track speed difference between ${lapA.name} and ${lapB.name}`}
          onPointerDown={beginDrag}
          onPointerMove={drag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={resetView}
          onContextMenu={event => event.preventDefault()}
          ref={svgRef}
        >
          <defs>
            <linearGradient id={markerId} x1="0" x2="1">
              <stop offset="0" stopColor={lapA.accent} />
              <stop offset="0.5" stopColor={lapA.accent} />
              <stop offset="0.5" stopColor={lapB.accent} />
              <stop offset="1" stopColor={lapB.accent} />
            </linearGradient>
          </defs>
          {model.elevationRangeM > 0.5 && <polyline points={groundPath} strokeWidth={sw(8)}
            strokeDasharray={`${sw(2)} ${sw(10)}`} className={styles.groundTrack} />}
          <polyline points={roadPath} strokeWidth={sw(RIBBON_STROKE.shadow)}
            className={styles.trackShadow} />
          {highlightPath && (
            <path d={highlightPath} strokeWidth={sw(31)} className={styles.cornerHighlight} />
          )}
          {buckets.map((bucket, bucketIndex) => (
            <g key={bucketIndex}>
              {bucket.road && (
                <path d={bucket.road} strokeWidth={sw(RIBBON_STROKE.road)} className={styles.roadSegment} />
              )}
              {[...bucket.ghostFill].map(([color, d]) => (
                <path key={`ghostfill-${color}`} d={d} fill={color} className={styles.ribbonGhostFill} />
              ))}
              {[...bucket.ghost].map(([color, d]) => (
                <path key={`ghost-${color}`} d={d} stroke={color}
                  strokeWidth={sw(RIBBON_STROKE.ghostEdge)} className={styles.ribbonGhost} />
              ))}
              {[...bucket.fill].map(([color, d]) => (
                <path key={`fill-${color}`} d={d} fill={color} className={styles.ribbonFill} />
              ))}
              {[...bucket.edge].map(([color, d]) => (
                <path key={`edge-${color}`} d={d} stroke={color}
                  strokeWidth={sw(RIBBON_STROKE.edge)} className={styles.ribbonEdge} />
              ))}
            </g>
          ))}
          {/* B's own racing line on the road surface. Always drawn, because where
              the two cars put the car is context for every mode, and promoted
              when Line mode makes it the subject. */}
          <polyline
            points={lineBPath}
            stroke={lapB.accent}
            strokeWidth={sw(RIBBON_STROKE.lineB * (heightMode === 'line' ? 1.6 : 1))}
            strokeDasharray={`${sw(9)} ${sw(7)}`}
            opacity={heightMode === 'line' ? 0.95 : 0.4}
            className={styles.lineB}
          />
          {clippedPath && (
            <path d={clippedPath} strokeWidth={sw(RIBBON_STROKE.clipped)}
              strokeDasharray={`${sw(3)} ${sw(4)}`} className={styles.ribbonClipped} />
          )}
          {leading && stationFrac > 0.002 && (
            <>
              <path
                d={`M${projection.road[wholeStation].x},${projection.road[wholeStation].y}`
                  + `L${roadPoint.x},${roadPoint.y}L${topPoint.x},${topPoint.y}`
                  + `L${projection.top[wholeStation].x},${projection.top[wholeStation].y}Z`}
                fill={leading.color} className={styles.ribbonFill}
              />
              <path
                d={`M${projection.top[wholeStation].x},${projection.top[wholeStation].y}`
                  + `L${topPoint.x},${topPoint.y}`}
                stroke={leading.color} strokeWidth={sw(RIBBON_STROKE.edge)}
                className={styles.ribbonEdge}
              />
            </>
          )}
          <line x1={roadPoint.x} y1={roadPoint.y} x2={topPoint.x} y2={topPoint.y} strokeWidth={markerSw(RIBBON_STROKE.cursorWall)} className={styles.cursorWall} />
          <circle cx={topPoint.x} cy={topPoint.y} r={markerR(5)} className={styles.cursorCap} />
          <circle cx={roadPoint.x} cy={roadPoint.y} r={markerR(20)}
            strokeWidth={markerSw(2)} className={styles.markerHalo} />
          <circle cx={roadPoint.x} cy={roadPoint.y} r={markerR(12)} strokeWidth={markerSw(3)}
            fill={`url(#${markerId})`} className={styles.marker} />
          {/* Pinned to the viewport corner so zooming does not pan it away. The
              bar is a scene measurement and grows with the track; the ticks and
              label divide it back out to stay a constant size on screen. */}
          <g
            className={styles.ruler}
            transform={`translate(${view.x + 26 / view.zoom}, ${view.y + view.height - 28 / view.zoom})`}
          >
            <line x1="0" y1="0" x2="0" y2={-rulerPx} />
            <line x1={-5 / view.zoom} y1="0" x2={5 / view.zoom} y2="0" />
            <line x1={-5 / view.zoom} y1={-rulerPx} x2={5 / view.zoom} y2={-rulerPx} />
            <text x={10 / view.zoom} y={-rulerPx + 4 / view.zoom} fontSize={15 / view.zoom}>{label}</text>
          </g>
        </svg>

        {/* Zooming in trades the overview away, which is the whole point of the
            panel — so hand it back as an inset. Drawn from the *same* projection
            at full extent, which is what makes the viewport box exact rather
            than an approximation of a differently-angled plan view. */}
        {view.zoom > 1.01 && (
          <svg className={styles.minimap} viewBox={`0 0 ${VIEW_BOX.width} ${VIEW_BOX.height}`} aria-hidden="true">
            <polyline points={roadPath} className={styles.minimapRoad} />
            <rect
              x={view.x} y={view.y} width={view.width} height={view.height}
              className={styles.minimapView}
            />
            <circle cx={roadPoint.x} cy={roadPoint.y} r="16" className={styles.minimapCursor} />
          </svg>
        )}

        <div className={styles.runningGap}>
          <span>{gap.lead}</span>
          <strong>{gap.value}</strong>
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.controlGroup}>
          <span>Height</span>
          {Object.values(HEIGHT_MODES).map(mode => (
            <button
              key={mode.key}
              type="button"
              className={heightMode === mode.key ? styles.controlActive : ''}
              onClick={() => setHeightMode(mode.key)}
              title={mode.hint}
            >{mode.label}</button>
          ))}
        </div>
        <div className={styles.controlGroup}>
          <span>Scale</span>
          {EXAGGERATIONS.map(value => (
            <button
              key={value}
              type="button"
              className={exaggeration === value ? styles.controlActive : ''}
              onClick={() => setExaggeration(value)}
              title={value === 'auto'
                ? 'Fit this pair. Always visible, but heights are not comparable between pairs.'
                : 'Fixed scale, so ribbon height means the same thing on every comparison.'}
            >{value === 'auto' ? 'Auto' : `${value}×`}</button>
          ))}
          <em>full height = {label}</em>
        </div>
        <div className={styles.controlGroup}>
          <span>View</span>
          {CAMERA_PRESETS.map(preset => (
            <button
              key={preset.key}
              type="button"
              className={Math.abs(viewAngles.pitch - preset.pitch) < 1 ? styles.controlActive : ''}
              onClick={() => setViewAngles(angles => ({ ...angles, pitch: preset.pitch }))}
            >{preset.label}</button>
          ))}
          {/* A toggle, not a fourth preset: it composes with the pitch above. */}
          <button
            type="button"
            className={camera.follow ? styles.controlActive : ''}
            onClick={() => setCamera(current => ({
              ...current,
              follow: !current.follow,
              // Following at full-track zoom would just be the view you already
              // had, so opening it in also moves the camera in.
              zoom: !current.follow && current.zoom <= 1 ? FOLLOW_ZOOM : current.zoom,
            }))}
            title="Keep the camera on the cars as the lap plays. Track orientation stays fixed; scroll to set how close."
          >Follow</button>
          <button type="button" onClick={resetView} title="Reset angle, zoom and follow.">Reset</button>
          {view.zoom > 1.01 && <em>{view.zoom.toFixed(1)}×</em>}
        </div>
        {canExportVideo() && (
          <div className={`${styles.controlGroup} ${styles.exportGroup}`}>
            {exportJob ? (
              <>
                <em>
                  {exportJob.phase === 'encode' ? 'Encoding\u2026'
                    : `Rendering ${exportJob.total ? Math.round((exportJob.done / exportJob.total) * 100) : 0}%`}
                </em>
                <button type="button" onClick={() => { cancelExport.current = true; }}>Cancel</button>
              </>
            ) : (
              <button
                type="button"
                onClick={runExport}
                title={`Render the playback to an MP4 at 1080p30, at the selected ${rate}\u00d7 speed.`}
              >Export MP4</button>
            )}
          </div>
        )}
      </div>
      {exportNote && <div className={styles.exportNote}>{exportNote}</div>}

      {!compact && model.sectors.length > 0 && (
        <div className={styles.sectors}>
          <button
            type="button"
            className={`${styles.sectorSort} ${sectorsByLoss ? styles.controlActive : ''}`}
            onClick={() => setSectorsByLoss(value => !value)}
            title="Order sections by the biggest time difference instead of track order"
          >{sectorsByLoss ? 'Biggest' : 'In order'}</button>
          {orderedSectors.map((item) => {
            const readout = sectorReadout(item, lapA, lapB);
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => { setPlaying(false); seekToFraction(item.startFrac); }}
                className={styles[`sector-${item.state}`]}
                // Compact in the cell so eleven sections still fit, with the
                // spelled-out result a hover away. Color is what says who.
                title={item.state === 'done' ? readout.text : item.label}
              >
                <span>{item.label}</span>
                <strong style={item.state === 'done' ? { color: readout.color } : undefined}>
                  {item.state === 'done' ? readout.compact : '—'}
                </strong>
                {item.state === 'active' && (
                  <i className={styles.sectorProgress} style={{ transform: `scaleX(${item.progress})` }} />
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.transport}>
        <button
          type="button"
          onClick={() => {
            if (elapsed >= model.durationSec - 0.01) seek(0);
            lastFrameRef.current = null;
            setPlaying(value => !value);
          }}
          aria-label={playing ? 'Pause track difference' : 'Play track difference'}
        >{playing ? 'Ⅱ' : '▶'}</button>
        <button type="button" onClick={() => { setPlaying(false); seek(0); }} aria-label="Restart track difference">↺</button>
        <span className={styles.clock}>{elapsed.toFixed(1)} / {model.durationSec.toFixed(1)}s</span>
        <input
          type="range" min="0" max={model.durationSec} step="0.01" value={elapsed}
          onChange={event => { setPlaying(false); seek(event.target.value); }}
          aria-label="Track difference position"
        />
        <div className={styles.rates}>
          {[1, 2, 4].map(value => (
            <button key={value} type="button" className={rate === value ? styles.rateActive : ''} onClick={() => setRate(value)}>{value}×</button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TrackDifferencePanel({
  session, lapTraces, selectedLaps, driverName1, compareDrivers = [], customColors1,
  sectors = [], elevationProfile = [], unitPrefs = {}, preferredPair = null, primarySfOffsetM = null,
  onFetchTrace = null, loadingTraces = null,
}) {
  const options = useMemo(() => collectTrackDifferenceLaps({
    session, lapTraces, selectedLaps, driverName1, compareDrivers, customColors1,
    primarySfOffsetM, onFetchTrace, loadingTraces,
  }), [session, lapTraces, selectedLaps, driverName1, compareDrivers, customColors1,
    primarySfOffsetM, onFetchTrace, loadingTraces]);
  const defaults = useMemo(() => pickDefaultTrackDifferencePair(options), [options]);
  const [keyA, setKeyA] = useState(null);
  const [keyB, setKeyB] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const selectionContextRef = useRef(null);
  const appliedPreferredRef = useRef(null);

  const selectionContext = useMemo(() => Array.from(new Set(
    options.map(item => `${item.driverKey}:${item.sessionId || ''}`),
  )).join('|'), [options]);

  useEffect(() => {
    const preferredA = preferredPair?.a
      ? options.find(item => item.sessionId === preferredPair.a.sessionId && item.lapIndex === preferredPair.a.lapIndex)
      : null;
    const preferredB = preferredPair?.b
      ? options.find(item => item.sessionId === preferredPair.b.sessionId && item.lapIndex === preferredPair.b.lapIndex)
      : null;
    const preferredChanged = !!(preferredA && preferredB && preferredPair !== appliedPreferredRef.current);
    const contextChanged = selectionContext !== selectionContextRef.current;
    selectionContextRef.current = selectionContext;
    if (preferredChanged) appliedPreferredRef.current = preferredPair;
    else if (!preferredPair) appliedPreferredRef.current = null;
    const currentA = options.find(item => item.key === keyA);
    const currentB = options.find(item => item.key === keyB);
    let nextA = currentA || defaults[0] || null;
    let nextB = currentB?.key !== nextA?.key ? currentB : null;
    if (contextChanged) [nextA, nextB] = defaults;
    if (preferredChanged) [nextA, nextB] = [preferredA, preferredB];
    nextB = nextB
      || defaults.find(item => item.key !== nextA?.key)
      || options.find(item => item.key !== nextA?.key)
      || null;
    if (nextA?.key !== keyA) setKeyA(nextA?.key || null);
    if (nextB?.key !== keyB) setKeyB(nextB?.key || null);
    if (preferredChanged || contextChanged) setLoadError(null);
  }, [options, defaults, preferredPair, selectionContext, keyA, keyB]);

  const lapA = options.find(item => item.key === keyA) || null;
  const lapB = options.find(item => item.key === keyB) || null;
  const sfMismatch = lapA && lapB && lapA.sessionId !== lapB.sessionId
    && [lapA.sfOffsetM, lapB.sfOffsetM].some(value => value != null && value >= SF_MISMATCH_WARN_M);
  const chooseLap = async (key, setKey) => {
    setKey(key);
    setLoadError(null);
    const option = options.find(item => item.key === key);
    if (option && !option.trace && option.fetchTrace) {
      const loaded = await option.fetchTrace();
      if (!loaded) setLoadError(`Could not load ${option.name} Lap ${option.lapNo} telemetry.`);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadDefaultTrace = async (option) => {
      if (!option || option.trace || option.isLoading || !option.fetchTrace) return;
      const loaded = await option.fetchTrace();
      if (!cancelled && !loaded) {
        setLoadError(`Could not load ${option.name} Lap ${option.lapNo} telemetry.`);
      }
    };
    loadDefaultTrace(lapA);
    loadDefaultTrace(lapB);
    return () => { cancelled = true; };
  }, [lapA?.key, lapB?.key]);

  return (
    <div className={styles.panel}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>Track Difference</span>}>
        <div className={styles.selectors}>
          <label>
            <span style={{ background: lapA?.accent || '#777' }} />
            <select value={keyA || ''} onChange={event => chooseLap(event.target.value, setKeyA)} aria-label="First comparison lap">
              {groupedLapOptions(options, keyB)}
            </select>
          </label>
          <span className={styles.versus}>vs</span>
          <label>
            <span style={{ background: lapB?.accent || '#777' }} />
            <select value={keyB || ''} onChange={event => chooseLap(event.target.value, setKeyB)} aria-label="Second comparison lap">
              {groupedLapOptions(options, keyA)}
            </select>
          </label>
        </div>
        {/* Portaled into the title bar, which is a drag handle — already covered
            by WindowFrame's Rnd `cancel`, so no mousedown guard here. */}
        <HelpButton panel="trackdifference" page="sessions" className={styles.helpBtn} />
      </PanelHeader>
      {loadError ? (
        <div className={styles.empty}>{loadError}</div>
      ) : !lapA?.trace || !lapB?.trace ? (
        <div className={styles.empty}>Loading lap telemetry…</div>
      ) : sfMismatch ? (
        <div className={styles.empty}>
          These sessions use different start/finish lines, so a same-position comparison would match the wrong parts of the track.
        </div>
      ) : (
        <TrackDifferenceView
          lapA={lapA} lapB={lapB} sectors={sectors}
          elevationProfile={elevationProfile} unitPrefs={unitPrefs}
        />
      )}
    </div>
  );
}
